import React from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Edit, MoreHorizontal, ToggleRight, User as UserIcon, Key, Crown, Shield, Settings } from "lucide-react";
import { SystemUser } from "@/pages/user/hooks/useUserManagement";
import { UserRole as UserRoleEnum } from "@/types/enums";
import { DataTable, DataTableColumn } from "@/components/ui/DataTable";
import { StatusBadge, getStatusColor } from "@/utils/statusUtils";

interface UserTableProps {
  users: SystemUser[];
  viewUserProfile: (user: SystemUser) => void;
  onEditUser: (user: SystemUser) => void;
  onResetPassword: (user: SystemUser) => void;
}

export const UserTable = React.memo(function UserTable({
  users,
  viewUserProfile,
  onResetPassword
}: UserTableProps) {


  // Enhanced role formatting with icons
  const getRoleDisplay = (role: string): { label: string; icon: React.ReactNode; color: string } => {
    switch (role) {
      case UserRoleEnum.DEPARTMENT_HEAD:
        return { 
          label: "Department Head", 
          icon: <Crown className="h-3 w-3" />, 
          color: "text-blue-600" 
        };
      case UserRoleEnum.PLANT_MANAGER:
        return { 
          label: "Plant Manager", 
          icon: <Shield className="h-3 w-3" />, 
          color: "text-purple-600" 
        };
      case UserRoleEnum.HR:
        return { 
          label: "HR", 
          icon: <UserIcon className="h-3 w-3" />, 
          color: "text-green-600" 
        };
      case UserRoleEnum.SUPER_ADMIN:
        return { 
          label: "Super Admin", 
          icon: <Settings className="h-3 w-3" />, 
          color: "text-orange-600" 
        };
      case UserRoleEnum.APP_OWNER:
        return { 
          label: "App Owner", 
          icon: <Crown className="h-3 w-3" />, 
          color: "text-red-600" 
        };
      default:
        return { 
          label: role, 
          icon: <UserIcon className="h-3 w-3" />, 
          color: "text-gray-600" 
        };
    }
  };

  // Enhanced avatar with better fallback
  const getInitials = (name: string): string => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  // Define enhanced columns for the data table
  const columns: DataTableColumn<SystemUser>[] = [
    {
      key: 'name',
      header: 'User',
      sortable: true,
      searchable: true,
      className: 'text-left',
      render: (value, user) => (
        <div className="flex items-center space-x-3">
          <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background border-2 border-border">
            <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 font-semibold">
              {getInitials(user.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-foreground truncate">{user.name}</span>
            <span className="text-sm text-muted-foreground truncate">{user.email}</span>
          </div>
        </div>
      ),
      exportRender: (value, user) => `${user.name} (${user.email})`
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      searchable: true,
      render: (value, user) => {
        const departmentDisplay = Array.isArray(user.department) 
          ? (user.department.length > 0 ? user.department.join(', ') : "All Departments")
          : (user.department || "All Departments");
        
        return (
          <div className="text-center break-words whitespace-normal">
            <Badge variant="outline" className={`whitespace-normal ${getStatusColor('neutral', 'light')}`}>
              {departmentDisplay}
            </Badge>
          </div>
        );
      },
      exportRender: (value, user) => {
        if (Array.isArray(user.department)) {
          return user.department.length > 0 ? user.department.join(', ') : 'All Departments';
        }
        return user.department || 'All Departments';
      }
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      searchable: true,
      render: (value) => {
        const roleDisplay = getRoleDisplay(String(value));
        return (
          <div className="text-center">
            <Badge variant="secondary" className={`px-3 py-1.5 ${roleDisplay.color} bg-white border font-medium`}>
              <span className="flex items-center gap-1.5">
                {roleDisplay.icon}
                {roleDisplay.label}
              </span>
            </Badge>
          </div>
        );
      },
      exportRender: (value) => getRoleDisplay(String(value)).label
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      searchable: true,
      render: (value) => {
        const status = String(value);
        return (
          <div className="text-center">
            <StatusBadge 
              status={status}
              className="px-3 py-1.5 font-medium"
            />
          </div>
        );
      },
      exportRender: (value) => String(value)
    },
    {
      key: 'id',
      header: 'Actions',
      sortable: false,
      searchable: false,
      render: (value, user) => (
        <div className="text-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="h-9 w-9 p-0 hover:bg-muted/80 focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className="sr-only">Open menu for {user.name}</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={() => viewUserProfile(user)}
                className="cursor-pointer hover:bg-muted/80"
              >
                <UserIcon className="mr-2 h-4 w-4 text-blue-600" />
                View Profile
              </DropdownMenuItem>
              <DropdownMenuItem 
                disabled
                className="cursor-not-allowed opacity-50 text-muted-foreground"
              >
                <Edit className="mr-2 h-4 w-4 text-muted-foreground" />
                Edit User
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                disabled
                className="cursor-not-allowed opacity-50 text-muted-foreground"
              >
                <ToggleRight className="mr-2 h-4 w-4 text-muted-foreground" />
                Change Status
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onResetPassword(user)}
                className="cursor-pointer hover:bg-muted/80"
              >
                <Key className="mr-2 h-4 w-4 text-red-600" />
                Reset Password
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      exportRender: () => 'Actions Available'
    }
  ];

  return (
    <div className="rounded-lg border bg-card">
      <DataTable
        data={users}
        columns={columns}
        title="System Users"
        config={{
          searchPlaceholder: "Search by name, email, role, department, or status...",
          defaultSortField: 'name',
          defaultSortDirection: 'asc',
          rowsPerPage: 10,
          showSearch: true,
          showColumnVisibility: true,
          showExport: true,
          showRefresh: false
        }}
      />
    </div>
  );
}); 